import { sanitizeUpstreamUrl } from '@/utils/app-logger/redaction/sanitizeUrl';

/** Bounded, always-on summary of a failed upstream call, safe for prod logs. */
export type UpstreamDiagnostics = {
    status?: number;
    path?: string;
    errorCode?: string;
};

// Stable identifiers only (e.g. `INVALID_PARAMETER`); free-form upstream text can carry PII.
const ERROR_CODE_REGEX = /^[A-Za-z0-9_.:-]{1,64}$/;

export const isErrorCodeIdentifier = (value: unknown): value is string =>
    typeof value === 'string' && ERROR_CODE_REGEX.test(value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

// RESTDataSource non-2xx errors carry `extensions.response = { url, status, statusText, body }`.
const isRestErrorResponse = (value: Record<string, unknown>): boolean =>
    typeof value.status === 'number' && 'body' in value;

const readErrorCode = (body: unknown): string | undefined => {
    if (!isRecord(body)) return undefined;
    return [body.errorCode, body.error].find(isErrorCodeIdentifier);
};

const compact = ({ status, path, errorCode }: UpstreamDiagnostics) => {
    const diagnostics: UpstreamDiagnostics = {
        ...(status === undefined ? {} : { status }),
        ...(path === undefined ? {} : { path }),
        ...(errorCode === undefined ? {} : { errorCode }),
    };
    return Object.keys(diagnostics).length > 0 ? diagnostics : undefined;
};

/**
 * Derives upstream diagnostics from a data-source `response` payload — either the
 * RESTDataSource error shape or a parsed upstream body. `upstreamPath` wins over the URL.
 */
export const deriveUpstreamDiagnostics = (
    response: unknown,
    upstreamPath?: string,
): UpstreamDiagnostics | undefined => {
    const path = sanitizeUpstreamUrl(upstreamPath);

    if (!isRecord(response)) return compact({ path });

    if (isRestErrorResponse(response)) {
        return compact({
            status: response.status as number,
            path: path ?? sanitizeUpstreamUrl(response.url),
            errorCode: readErrorCode(response.body),
        });
    }

    return compact({ path, errorCode: readErrorCode(response) });
};

// Any GraphQLError can carry a hand-built `upstream`, so re-apply the same bounds on read.
const sanitizeUpstream = (upstream: Record<string, unknown>): UpstreamDiagnostics | undefined =>
    compact({
        status: Number.isInteger(upstream.status) ? (upstream.status as number) : undefined,
        path: sanitizeUpstreamUrl(upstream.path),
        errorCode: readErrorCode({ errorCode: upstream.errorCode }),
    });

/**
 * Resolves upstream diagnostics for a GraphQL error's `extensions`, preferring the
 * `upstream` field set by `throwGraphQLError` over a raw RESTDataSource `response`.
 */
export const getUpstreamDiagnostics = (
    extensions: Record<string, unknown> | undefined,
): UpstreamDiagnostics | undefined => {
    if (!extensions) return undefined;
    if (isRecord(extensions.upstream)) return sanitizeUpstream(extensions.upstream);
    return deriveUpstreamDiagnostics(extensions.response);
};
